"use client";

import React, { useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";

import {
  MdChevronRight,
  MdOutlineMap,
  MdOutlineDescription,
  MdOutlineMiscellaneousServices,
  MdOutlineInfo,
  MdOutlineListAlt,
  MdOutlinePermMedia,
  MdPeopleAlt,
} from "react-icons/md";

import styles from "./new-listing.module.css";
import BasicInfoSection from "./components/basicInfoSection";
import LivePreviewCard from "./components/livePreviewCard";
import FloatingBottomBar from "./components/floatingBottomBar";
import { useListingStore } from "./useListingStore";
import { ListingProvider } from "./listingContex";
import LocationSection from "./components/locationSection";
import PricingAndContentSection from "./components/pricingContentSection";
import PropertySpecsSection from "./components/propertySpecsSection";
import AmenitiesSection from "./components/amenitiesSection";
import MediaSection from "./components/mediaSection";
import AgencySection from "./components/agencySection";
import { useAppLocale } from "@/hooks/useAppLocale";

interface NewListingComponentProps {
  getRedirectPath: (listingId: number | string) => string;
  storeHook?: any; // 🚀 Allow injecting different stores!
  isEditMode?: boolean;
}

export default function NewListingComponent({
  getRedirectPath,
  storeHook = useListingStore, // 🚀 Default to the "Add" store
  isEditMode = false,
}: NewListingComponentProps) {
  const t = useTranslations("NewListing");
  const locale = useAppLocale();

  // 🚀 Call the injected store hook dynamically
  const { initializeListing, data, isUserAgencyOwner } = storeHook();

  useEffect(() => {
    // Only initialize a new draft if we are NOT editing
    if (!isEditMode && initializeListing) {
      initializeListing(locale);
    }
  }, [isEditMode, locale, initializeListing]);
  return (
    <ListingProvider
      storeHook={storeHook}
      getRedirectPathAction={getRedirectPath}
    >
      <main className={styles.main}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            Add <strong>New Listing</strong>
          </h1>
          <p className={styles.subtitle}>
            Create and publish your property to reach the right buyers
          </p>
          <div className={styles.goldAccent}></div>
        </header>

        <div className={styles.gridContainer}>
          <div className={styles.leftColumn}>
            {/* Added IDs to sections for the navigation links */}
            <div id="basic-info">
              <BasicInfoSection />
            </div>

            {/* Placeholders for the sections to be implemented */}
            <div id="location">
              <LocationSection />
            </div>
            <div id="pricing">
              <PricingAndContentSection />
            </div>
            <div id="specs">
              <PropertySpecsSection />
            </div>
            <div id="media">
              <MediaSection />
            </div>
            <div id="facilities">
              <AmenitiesSection />
            </div>
            {isUserAgencyOwner && data?.agency_id && (
              <div id="agency-publishing">
                <AgencySection />
              </div>
            )}
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.stickyWrapper}>
              <div className={styles.detailsMenu}>
                <h3 className={styles.menuTitle}>Property Details</h3>
                <ul className={styles.menuList}>
                  <MenuListItem
                    icon={<MdOutlineInfo size={20} />}
                    text="Basic Information"
                    targetId="basic-info"
                  />
                  <MenuListItem
                    icon={<MdOutlineMap size={20} />}
                    text="Location & Map"
                    targetId="location"
                  />
                  <MenuListItem
                    icon={<MdOutlineDescription size={20} />}
                    text="Pricing & Description"
                    targetId="pricing"
                  />
                  <MenuListItem
                    icon={<MdOutlineListAlt size={20} />}
                    text="Property Specs"
                    targetId="specs"
                  />
                  <MenuListItem
                    icon={<MdOutlinePermMedia size={20} />}
                    text="Media & Tours"
                    targetId="media"
                  />
                  <MenuListItem
                    icon={<MdOutlineMiscellaneousServices size={20} />}
                    text="Facilities & Amenities"
                    targetId="facilities"
                  />
                  {isUserAgencyOwner && data?.agency_id && (
                    <MenuListItem
                      icon={<MdPeopleAlt size={20} />}
                      text="Agency"
                      targetId="agency-publishing"
                    />
                  )}
                </ul>
              </div>
              <LivePreviewCard data={data} t={t} />
            </div>
          </div>
        </div>

        {!isEditMode && <FloatingBottomBar />}
      </main>
    </ListingProvider>
  );
}

function MenuListItem({
  icon,
  text,
  targetId,
}: {
  icon: React.ReactNode;
  text: string;
  targetId: string;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      // Smooth scroll to element with offset for the header/top padding
      const offset = 40;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <li className={styles.menuItem} onClick={handleClick}>
      <a href={`#${targetId}`} className={styles.menuItemLink}>
        <div className={styles.menuItemLeft}>
          {icon}
          <span>{text}</span>
        </div>
        <MdChevronRight size={20} className={styles.menuChevron} />
      </a>
    </li>
  );
}
